import { motion } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import {
  FileText,
  Shield,
  CreditCard,
  AlertCircle,
  Scale,
  Lock,
  RefreshCw,
  Mail,
  ArrowLeft,
  ChevronRight,
  UserCheck,
  Globe,
} from "lucide-react";

const Section = ({ icon: Icon, title, children, isDark, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className={`rounded-xl border p-5 sm:p-6 ${
      isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"
    }`}
  >
    <div className="flex items-center gap-2.5 mb-3">
      <div className={`p-1.5 rounded-lg ${isDark ? "bg-blue-500/10" : "bg-blue-50"}`}>
        <Icon size={15} className={isDark ? "text-blue-400" : "text-blue-600"} />
      </div>
      <h2 className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
        {title}
      </h2>
    </div>
    <div className={`text-[12.5px] leading-relaxed space-y-2.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
      {children}
    </div>
  </motion.div>
);

const Item = ({ title, children, isDark }) => (
  <div className={`rounded-lg p-4 mt-2 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}>
    <div className="flex items-center gap-2 mb-2">
      <ChevronRight size={12} className={isDark ? "text-blue-400" : "text-blue-600"} />
      <h3 className={`text-[13px] font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>{title}</h3>
    </div>
    {children}
  </div>
);

const TermsOfService = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const location = useLocation();
  const isPublic = location.pathname === "/terms";

  return (
    <div className={`min-h-screen h-full overflow-y-auto p-4 md:p-6 ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-4">
        {isPublic && (
          <Link to="/login" className={`inline-flex items-center gap-1.5 text-[12px] font-medium transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}>
            <ArrowLeft size={14} />
            Terug naar login
          </Link>
        )}

        {/* Header */}
        <div className="mb-2">
          <div className="flex items-center gap-2.5 mb-1">
            <div className={`p-2 rounded-xl ${isDark ? "bg-blue-500/10" : "bg-blue-50"}`}>
              <Scale size={18} className={isDark ? "text-blue-400" : "text-blue-600"} />
            </div>
            <div>
              <h1 className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>Gebruiksvoorwaarden</h1>
              <p className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Laatst bijgewerkt: 15 mei 2026</p>
            </div>
          </div>
        </div>

        {/* Inleiding */}
        <Section icon={FileText} title="Inleiding" isDark={isDark} delay={0.05}>
          <p>
            Welkom bij Zichtbaar Studio. Door gebruik te maken van ons dashboard-platform, leerplatform of andere diensten, gaat u akkoord met deze gebruiksvoorwaarden. Lees deze aandachtig door.
          </p>
          <p>
            Zichtbaar Studio is een product van Zichtbaar BV, gevestigd in België. Onze diensten zijn uitsluitend bestemd voor zakelijke gebruikers (B2B). Door toegang te krijgen tot ons platform bevestigt u dat u handelt als bedrijf of zelfstandige.
          </p>
        </Section>

        {/* Gebruik van het platform */}
        <Section icon={Globe} title="Gebruik van het platform" isDark={isDark} delay={0.1}>
          <p>U mag het platform gebruiken voor de doeleinden waarvoor het is ontworpen, namelijk het beheren van uw online aanwezigheid, content, bestellingen, reservaties en aanverwante bedrijfsactiviteiten.</p>
          <Item title="Toegestaan gebruik" isDark={isDark}>
            <ul className={`text-[12px] list-disc pl-4 space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <li>Beheren van uw bedrijfscontent via het dashboard</li>
              <li>Verbinden van sociale media-accounts voor analyse</li>
              <li>Gebruik van AI-functies binnen de toegewezen limieten</li>
              <li>Exporteren van uw eigen gegevens</li>
            </ul>
          </Item>
          <Item title="Verboden gebruik" isDark={isDark}>
            <ul className={`text-[12px] list-disc pl-4 space-y-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <li>Het platform gebruiken voor illegale activiteiten of het verspreiden van schadelijke content</li>
              <li>Toegangsgegevens delen met derden buiten uw organisatie</li>
              <li>Reverse engineering, scraping of geautomatiseerd misbruik van de API</li>
              <li>Handelingen die de stabiliteit of veiligheid van het platform in gevaar brengen</li>
            </ul>
          </Item>
        </Section>

        {/* Accounts & toegang */}
        <Section icon={UserCheck} title="Accounts & toegang" isDark={isDark} delay={0.15}>
          <p>U bent verantwoordelijk voor het veilig bewaren van uw inloggegevens en voor alle activiteiten die plaatsvinden onder uw account.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Meld verlies of diefstal van inloggegevens onmiddellijk via <strong className={isDark ? "text-gray-300" : "text-gray-700"}>info@zichtbaarstudio.be</strong></li>
            <li>Accounts zijn niet overdraagbaar zonder schriftelijke toestemming</li>
            <li>Wij behouden het recht om accounts op te schorten bij vermoeden van misbruik</li>
            <li>Inactieve accounts kunnen na 12 maanden worden verwijderd, mits voorafgaande kennisgeving</li>
          </ul>
        </Section>

        {/* Betalingen */}
        <Section icon={CreditCard} title="Betalingen & abonnementen" isDark={isDark} delay={0.2}>
          <p>Onze diensten worden aangeboden op basis van een maandelijks of jaarlijks abonnement. Prijzen zijn vermeld exclusief btw, tenzij anders aangegeven.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Abonnementen worden automatisch verlengd tenzij u opzegt vóór de verlengingsdatum</li>
            <li>Opzegging dient schriftelijk te gebeuren met minimaal 30 dagen opzegtermijn</li>
            <li>Reeds betaalde periodes worden niet terugbetaald bij voortijdige opzegging</li>
            <li>AI-credits worden aangerekend per gebruik en zijn niet overdraagbaar naar volgende periodes</li>
            <li>Bij niet-betaling behouden wij het recht de toegang te blokkeren na een betalingsherinnering</li>
          </ul>
        </Section>

        {/* Intellectueel eigendom */}
        <Section icon={Lock} title="Intellectueel eigendom" isDark={isDark} delay={0.25}>
          <p>Alle rechten op het platform, de software, het ontwerp en de documentatie berusten bij Zichtbaar Studio.</p>
          <Item title="Uw content" isDark={isDark}>
            <p className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              U behoudt alle rechten op de content die u via het platform beheert (teksten, afbeeldingen, producten, etc.). Door content te uploaden verleent u Zichtbaar Studio een beperkte licentie om deze te verwerken en weer te geven in het kader van de dienstverlening.
            </p>
          </Item>
          <Item title="Ons platform" isDark={isDark}>
            <p className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Het is niet toegestaan het platform of onderdelen ervan te kopiëren, te distribueren of te gebruiken buiten de toegestane licentie zonder schriftelijke toestemming van Zichtbaar Studio.
            </p>
          </Item>
        </Section>

        {/* Aansprakelijkheid */}
        <Section icon={AlertCircle} title="Aansprakelijkheid & garanties" isDark={isDark} delay={0.3}>
          <p>Zichtbaar Studio streeft naar maximale beschikbaarheid en kwaliteit, maar kan geen ononderbroken service garanderen.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Wij zijn niet aansprakelijk voor indirecte schade, gederfde winst of dataverlies als gevolg van storingen</li>
            <li>AI-gegenereerde analyses en aanbevelingen zijn indicatief en geen professioneel advies</li>
            <li>Onze totale aansprakelijkheid is beperkt tot het bedrag dat u de afgelopen 3 maanden heeft betaald</li>
            <li>Wij zijn niet verantwoordelijk voor de inhoud van externe platforms (Meta, YouTube, TikTok) die via ons platform worden gesynchroniseerd</li>
          </ul>
        </Section>

        {/* Sociale media-integraties */}
        <Section icon={Shield} title="Sociale media-integraties" isDark={isDark} delay={0.33}>
          <p>Door een sociaal media-account te verbinden (Meta/Instagram, YouTube, TikTok) gaat u akkoord met de voorwaarden van het betreffende platform én met de volgende bepalingen:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>U bent de rechtmatige eigenaar of gemachtigde beheerder van het te verbinden account</li>
            <li>Wij slaan OAuth-tokens versleuteld op en geven deze nooit door aan derden</li>
            <li>U kunt de verbinding op elk moment verbreken via de instellingen van uw dashboard</li>
            <li>Bij het verbreken van de verbinding worden tokens onmiddellijk ingetrokken; historische data blijft bewaard voor analyse tenzij u expliciet verwijdering aanvraagt</li>
          </ul>
        </Section>

        {/* Wijzigingen */}
        <Section icon={RefreshCw} title="Wijzigingen aan de voorwaarden" isDark={isDark} delay={0.36}>
          <p>
            Wij behouden het recht deze gebruiksvoorwaarden te wijzigen. Bij wezenlijke wijzigingen brengen wij u minimaal 14 dagen van tevoren op de hoogte via e-mail of via een melding in het dashboard.
          </p>
          <p>
            Voortgezet gebruik van het platform na de ingangsdatum van de nieuwe voorwaarden geldt als aanvaarding ervan.
          </p>
        </Section>

        {/* Toepasselijk recht */}
        <Section icon={Scale} title="Toepasselijk recht" isDark={isDark} delay={0.39}>
          <p>
            Deze gebruiksvoorwaarden worden beheerst door het Belgisch recht. Bij geschillen zijn uitsluitend de rechtbanken van het gerechtelijk arrondissement waar Zichtbaar BV is gevestigd bevoegd, tenzij dwingend recht anders bepaalt.
          </p>
        </Section>

        {/* Contact */}
        <Section icon={Mail} title="Contact" isDark={isDark} delay={0.42}>
          <p>Voor vragen over deze gebruiksvoorwaarden kunt u contact opnemen via:</p>
          <div className={`mt-2 p-3 rounded-lg text-[12px] ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}>
            <p className={`font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Zichtbaar Studio</p>
            <p>E-mail: <a href="mailto:info@zichtbaarstudio.be" className={`underline ${isDark ? "text-blue-400" : "text-blue-600"}`}>info@zichtbaarstudio.be</a></p>
            <p>Website: <a href="https://zichtbaarstudio.be" target="_blank" rel="noopener noreferrer" className={`underline ${isDark ? "text-blue-400" : "text-blue-600"}`}>zichtbaarstudio.be</a></p>
          </div>
          {isPublic && (
            <p className="mt-3">
              <Link to="/privacy" className={`underline text-[12px] ${isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"}`}>
                Bekijk ook ons Privacybeleid
              </Link>
            </p>
          )}
        </Section>

        <p className={`text-center text-[11px] pb-6 ${isDark ? "text-gray-700" : "text-gray-400"}`}>
          &copy; {new Date().getFullYear()} Zichtbaar Studio &mdash; Alle rechten voorbehouden
        </p>
      </motion.div>
    </div>
  );
};

export default TermsOfService;
